import Pagination, { createPaginationPages } from 'components/Pagination';
import React, { useEffect, useRef, useState } from 'react';

import ArchiveHero from 'components/ArchiveHero';
import ArticleGridLayout from 'components/pages/magazine/ArticleGridLayout';
import { ListingWrapper } from 'views/Category';
import NoResultComponent from 'components/NoResultComponent';
import PropTypes from 'prop-types';
import { Search } from 'libs/Algolia-v2';
import SubCategoryList from 'components/SubCategoryList';
import colors from 'config/theme/colors';
import { extractQueryParams } from 'utils/query';
import { scrollTo } from 'utils/scrollFunctions';
import { transformArticleCard } from 'utils/dataTransformers/article';
import transformFlexibleBackground from 'libs/wordpress/utils/transformFlexibleBackground';
import useAboveBreakpoint from 'hooks/useAboveBreakpoint';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const MagazineArchive = ({ data }) => {
    /**
     *  Init
     */

    const { t } = useTranslation();
    const location = useLocation();
    const isAboveMd = useAboveBreakpoint('md');
    const query = extractQueryParams(location.search);
    const appID = useSelector(state => state.application.applicationId);

    // Initial archive settings
    const pageSize = 11;
    const [state, setState] = useState({
        articles: [],
        categories: [],
        hits: { visible: 0, total: 0 },
        loading: false,
        page: query.page ? parseInt(query.page, 10) : 1,
        pages: [],
    });

    // Extract and transform data
    const { breadcrumbs, hero, article_category_filters: rawCategoryFilters } = data;
    const { heading, background_media_flexible: backgroundFlexible, paragraph } = hero?.data || {};
    const transformedBackground = transformFlexibleBackground(backgroundFlexible);
    const mobileBackground = transformedBackground.mobile;
    const desktopBackground = transformedBackground.desktop;

    /**
     *  Helper functions
     */

    // Scroll to articles
    const articlesRef = useRef(null);
    const scrollToArticles = () => {
        articlesRef.current &&
            scrollTo({
                // -48 to algin properly with the category filter
                top: articlesRef.current.offsetTop - 48,
            });
    };

    // Handle pagination
    const handlePagination = (e, page) => {
        setState(prevState => ({
            ...prevState,
            page: page.name,
        }));
        scrollToArticles();
    };

    /**
     *  Category filters
     */

    // Prepare category filters
    const hiddenCategories = ['Uncategorized', 'Okategoriserad'];
    const categoryFilters = rawCategoryFilters
        ? rawCategoryFilters
              .filter(category => hiddenCategories.indexOf(category.name) === -1)
              .map(category => ({
                  id: category.term_id,
                  title: category.name,
              }))
        : [];

    // Create "See all" filter
    const filterAll = {
        title: t('articles.see_all'),
        to: `${location.pathname}`,
        current: state.categories.length === 0,
        onClick: () =>
            // Reset articles, hits and offset & update categoryfilter
            setState(prevState => ({
                ...prevState,
                articles: [],
                categories: [],
                hits: { visible: 0, total: 0 },
                page: 1,
                pages: [],
            })),
    };

    // Add button for each existing filter-category
    const subCategoryItems = categoryFilters
        .filter(filter => hiddenCategories.indexOf(filter.title) === -1)
        .sort((a, b) => a.title.localeCompare(b.title))
        .map(filter => ({
            id: filter.id,
            title: filter.title,
            to: `${location.pathname}`,
            current: state.categories.some(category => category === filter.id),
            onClick: () => {
                // Reset articles, hits and offset & update categoryfilter
                setState(prevState => ({
                    ...prevState,
                    articles: [],
                    categories: [filter.id],
                    hits: { visible: 0, total: 0 },
                    page: 1,
                    pages: [],
                }));
            },
        }));

    // Merge all filter-buttons into a single array
    const allCategoryFilters = [filterAll, ...subCategoryItems];

    /**
     *  Search/Filter handler
     */

    // Algolia filter search which returns an array with articles
    useEffect(() => {
        setState(prevState => ({ ...prevState, loading: true }));

        // Add application id to get the correct language
        const allFilters = [[`application_id: ${appID}`]];

        // Add category filters if available otherwise add default categories
        let filterCategories = [];

        if (state.categories.length) {
            filterCategories = state.categories.map(categoryID => `categories_ids: ${categoryID}`);
        } else {
            filterCategories = categoryFilters.map(category => `categories_ids: ${category.id}`);
        }

        // Check if filterCategories exists for preventing pushing an empty array
        if (filterCategories.length > 0) {
            allFilters.push(filterCategories);
        }

        const algoliaIndex = 'articles'; // Will use REACT_APP_ALGOLIA_PREFIX to create something like -> 'dev_articles'
        const searchstring = ''; // Empty searchstring = show all

        Search(algoliaIndex, searchstring, {
            filters: allFilters,
            hitsPerPage: pageSize,
            page: state.page,
        }).then(response => {
            // Update state
            if (response) {
                const result = response.results?.[0] || [];
                const newArticles = result.hits.map(transformArticleCard);
                const currentPage = result.page + 1;

                setState(prevState => ({
                    ...prevState,
                    articles: newArticles,
                    hits: { visible: result.hits.length, total: result.nbHits },
                    loading: false,
                    page: currentPage,
                    pages: createPaginationPages(currentPage, result.nbPages, location),
                }));
            }
        });
    }, [state.categories, state.page]);

    return (
        <>
            <ArchiveHero
                background={isAboveMd ? desktopBackground : mobileBackground}
                breadcrumbs={breadcrumbs}
                heroDescription={paragraph}
                heroTitle={heading}
                numberOfItems={state.hits.total}
                shortCutText={t('articles.show_articles')}
                onClick={scrollToArticles}
            />
            <ListingWrapper ref={articlesRef}>
                {subCategoryItems?.length > 1 && (
                    <SubCategoryList borderBottom={`1px solid ${colors.grey4}`} items={allCategoryFilters} />
                )}
                {state.hits.total > 0 ? (
                    <ArticleGridLayout articles={state.articles} />
                ) : (
                    <NoResultComponent
                        text={state.loading ? '' : t('articles.no_articles_found')}
                        loading={state.loading}
                    />
                )}
                {state.pages.length > 1 && (
                    <Pagination
                        currentIndex={state.page - 1}
                        handleClick={handlePagination}
                        maxSiblings={isAboveMd ? 2 : 1}
                        message={t('pagination.articles_pagination')}
                        mt={['56px', null, null, '112px']}
                        pageItemCount={state.hits.visible}
                        pageSize={pageSize}
                        pages={state.pages}
                        totalItemCount={state.hits.total}
                    />
                )}
            </ListingWrapper>
        </>
    );
};

MagazineArchive.propTypes = {
    data: PropTypes.object.isRequired, // CMS Data
};

export default MagazineArchive;
